import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { getRegisteredTables } from "@/features/adp/services/api-service";
import type { RegisteredTargetTable } from "@/features/adp/services/api-service.types";
import {
  closeOverlay,
  openOverlay,
  type RegisteredDestinationsOverlayData,
} from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Input } from "@/ui/input";
import { RadioGroup, RadioGroupItem } from "@/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Skeleton } from "@/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/table";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Plus,
  Search,
} from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import styles from "./RegisteredDestinationsTableList.module.scss";

const ROWS_PER_PAGE_OPTIONS = [2, 5, 10, 25];

function isRegisteredDestinationsOverlayData(
  value: unknown
): value is RegisteredDestinationsOverlayData {
  return Boolean(value && typeof value === "object");
}

const RegisteredDestinationsTableList = () => {
  const dispatch = useAppDispatch();
  const { isOpen, type, data } = useAppSelector((state) => state.overlay);
  const [searchValue, setSearchValue] = useState("");
  const [expandedTableIds, setExpandedTableIds] = useState<number[]>([]);
  const [selectedTableId, setSelectedTableId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(2);

  const isVisible = isOpen && type === "registered-destinations-table-list";
  const modalData = isVisible && isRegisteredDestinationsOverlayData(data) ? data : null;
  const targetDataSourceId = modalData?.targetDataSourceId ?? null;

  const { data: registeredTablesResponse, isPending, error } = useQuery({
    queryKey: ["registered-destination-tables", targetDataSourceId],
    queryFn: () => getRegisteredTables(targetDataSourceId ?? 0),
    enabled: isVisible && typeof targetDataSourceId === "number" && targetDataSourceId > 0,
  });

  const targetTables = registeredTablesResponse?.data?.listOfTargetFiles ?? [];
  const visibleTables = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) return targetTables;

    return targetTables.filter((table) =>
      table.table_name.toLowerCase().includes(normalizedSearch)
    );
  }, [searchValue, targetTables]);

  const totalPages = Math.max(1, Math.ceil(visibleTables.length / rowsPerPage));
  const pagedTables = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return visibleTables.slice(startIndex, startIndex + rowsPerPage);
  }, [page, rowsPerPage, visibleTables]);
  const isEveryVisibleTableExpanded =
    pagedTables.length > 0 &&
    pagedTables.every((table) => expandedTableIds.includes(table.target_table_id));

  useEffect(() => {
    if (isVisible) {
      setSearchValue("");
      setExpandedTableIds([]);
      setSelectedTableId(undefined);
      setPage(1);
      setRowsPerPage(2);
    }
  }, [isVisible]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleClose = () => dispatch(closeOverlay());

  const handleBack = () => {
    if (!modalData) {
      handleClose();
      return;
    }

    dispatch(openOverlay({
      type: "registered-destinations-details",
      data: modalData,
    }));
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(Number(value));
    setPage(1);
  };

  const toggleTable = (table: RegisteredTargetTable) => {
    setExpandedTableIds((current) =>
      current.includes(table.target_table_id)
        ? current.filter((id) => id !== table.target_table_id)
        : [...current, table.target_table_id]
    );
  };

  const toggleExpandAll = () => {
    const visibleIds = pagedTables.map((table) => table.target_table_id);
    setExpandedTableIds((current) => {
      if (isEveryVisibleTableExpanded) {
        return current.filter((id) => !visibleIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleIds]));
    });
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className="max-w-5xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className={styles.header}>
          <div className={styles.breadcrumb}>
            <Button
              type="button"
              variant="text"
              className={styles.backButton}
              onClick={handleBack}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <span className={styles.breadcrumbMuted}>Destination Details</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbCurrent}>Table List</span>
          </div>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <div className={styles.toolbar}>
            <DialogTitle className={styles.title}>Table List</DialogTitle>
            <div className={styles.actions}>
              <div className={styles.searchWrap}>
                <Search className={styles.searchIcon} />
                <Input
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search Table Name"
                  aria-label="Search table name"
                  className={styles.searchInput}
                />
              </div>
              <Button type="button" className={styles.addButton}>
                <Plus className="h-4 w-4" />
                New Table
              </Button>
            </div>
          </div>

          <div className={styles.tableFrame}>
            <div className={styles.tableHeaderRow}>
              <span>Table name</span>
              <div className={styles.headerActions}>
                <Button
                  type="button"
                  variant="text"
                  className={styles.expandAllButton}
                  onClick={toggleExpandAll}
                  disabled={pagedTables.length === 0}
                >
                  <ChevronsUpDown className="h-4 w-4" />
                  Expand All
                </Button>
              </div>
            </div>

            <RadioGroup
              value={selectedTableId}
              onValueChange={setSelectedTableId}
              className={styles.tableList}
            >
              {isPending && (
                <>
                  <Skeleton className={styles.tableSkeleton} />
                  <Skeleton className={styles.tableSkeleton} />
                </>
              )}

              {!isPending && error && (
                <p className={styles.stateText}>
                  {error instanceof Error ? error.message : "Failed to load registered tables."}
                </p>
              )}

              {!isPending && !error && pagedTables.length === 0 && (
                <p className={styles.stateText}>No registered tables found.</p>
              )}

              {!isPending && !error && pagedTables.map((table, index) => {
                const isExpanded = expandedTableIds.includes(table.target_table_id);
                const tableValue = String(table.target_table_id);

                return (
                  <div
                    key={table.target_table_id}
                    className={`${styles.tableItem} ${isExpanded ? styles.tableItemExpanded : ""}`}
                  >
                    <div className={styles.tableSummary}>
                      <RadioGroupItem
                        value={tableValue}
                        className={styles.radioItem}
                        aria-label={`Select ${table.table_name}`}
                        onClick={(event) => {
                          if (selectedTableId === tableValue) {
                            event.preventDefault();
                            setSelectedTableId("");
                          }
                        }}
                      />
                      <span className={styles.tableName}>
                        {index + 1 + (page - 1) * rowsPerPage}. {table.table_name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        className={styles.iconAction}
                        onClick={() => toggleTable(table)}
                      >
                        <ChevronsUpDown className="h-4 w-4" />
                        <span>{isExpanded ? "Collapse" : "Expand"}</span>
                      </Button>
                    </div>

                    {isExpanded && (
                      <div className={styles.fieldsTable}>
                        <Table>
                          <TableHeader className={styles.fieldsHeader}>
                            <TableRow>
                              <TableHead>Field Name <span className={styles.sortIcon}>{"\u2191\u2193"}</span></TableHead>
                              <TableHead>Data Type <span className={styles.sortIcon}>{"\u2191\u2193"}</span></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {table.fields.slice(0, 7).map((field) => (
                              <TableRow key={`${table.target_table_id}-${field.target_table_column_id}`}>
                                <TableCell>{field.column_name}</TableCell>
                                <TableCell>{field.data_type}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          <div className={styles.paginationBar}>
            <div className={styles.paginationGroup}>
              <span>Rows per page</span>
              <Select value={String(rowsPerPage)} onValueChange={handleRowsPerPageChange}>
                <SelectTrigger className={styles.rowsSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <span className={styles.pageText}>Page {page} of {totalPages}</span>

            <div className={styles.pageControls}>
              <Button type="button" variant="outline" size="icon" className={styles.pageButton} disabled={page <= 1} onClick={() => setPage(1)} aria-label="First page">
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" className={styles.pageButton} disabled={page <= 1} onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))} aria-label="Previous page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" className={styles.pageButton} disabled={page >= totalPages} onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))} aria-label="Next page">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" className={styles.pageButton} disabled={page >= totalPages} onClick={() => setPage(totalPages)} aria-label="Last page">
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className={styles.footer}>
          <Button type="button" variant="outline" className={styles.cancelButton} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="button" className={styles.saveButton} disabled={!selectedTableId}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegisteredDestinationsTableList;
